import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { logout, updateLastAction } from '../redux/authSlice';

const DEFAULT_TIMEOUT_MINUTES = 45;

const parseTimeoutMinutes = (raw) => {
  const parsed = Number.parseFloat(raw);
  if (!Number.isFinite(parsed) || parsed <= 0) return DEFAULT_TIMEOUT_MINUTES;
  return parsed;
};

// Configured at build time (CRA inlines REACT_APP_* vars), see Dockerfile / .env.local
export const INACTIVITY_TIMEOUT_MINUTES = parseTimeoutMinutes(
  process.env.REACT_APP_INACTIVITY_TIMEOUT_MINUTES
);
export const INACTIVITY_TIMEOUT = INACTIVITY_TIMEOUT_MINUTES * 60 * 1000;

// How often we re-check, and how rarely activity is written back to the store.
const CHECK_INTERVAL = 15 * 1000;
const STORE_WRITE_THROTTLE = 30 * 1000;

const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'wheel',
  'touchstart',
  'scroll',
];

/**
 * Logs the user out after INACTIVITY_TIMEOUT without real user interaction.
 * Mount once per layout (MainLayout / MarktLayout), never inside a page.
 */
export const useInactivityCheck = () => {
  const dispatch = useDispatch();
  const accessToken = useSelector((state) => state.auth.accessToken);
  const lastActionAt = useSelector((state) => state.auth.lastActionAt);

  // Local mirror, so pointer/key events don't dispatch (and re-persist) on every move.
  const lastActionRef = useRef(lastActionAt);
  const lastWriteRef = useRef(lastActionAt);

  useEffect(() => {
    // Login and token refresh set lastActionAt directly; adopt anything newer.
    if (lastActionAt && lastActionAt > (lastActionRef.current || 0)) {
      lastActionRef.current = lastActionAt;
      lastWriteRef.current = lastActionAt;
    }
  }, [lastActionAt]);

  useEffect(() => {
    if (!accessToken) return undefined;

    // A session persisted before this feature existed has no timestamp yet.
    if (!lastActionRef.current) {
      lastActionRef.current = Date.now();
      lastWriteRef.current = lastActionRef.current;
      dispatch(updateLastAction());
    }

    let expired = false;

    const isExpired = () =>
      Date.now() - lastActionRef.current >= INACTIVITY_TIMEOUT;

    const expire = () => {
      if (expired) return;
      expired = true;
      dispatch(logout());
    };

    const check = () => {
      if (isExpired()) expire();
    };

    const registerActivity = () => {
      if (expired) return;
      // The tab may have been asleep: expire before crediting the wake-up event.
      if (isExpired()) {
        expire();
        return;
      }
      const now = Date.now();
      lastActionRef.current = now;
      if (now - (lastWriteRef.current || 0) >= STORE_WRITE_THROTTLE) {
        lastWriteRef.current = now;
        dispatch(updateLastAction());
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') check();
    };

    // A rehydrated session can already be stale before the first tick runs.
    check();

    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, registerActivity, { passive: true })
    );
    document.addEventListener('visibilitychange', onVisibilityChange);
    const intervalId = setInterval(check, CHECK_INTERVAL);

    return () => {
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, registerActivity)
      );
      document.removeEventListener('visibilitychange', onVisibilityChange);
      clearInterval(intervalId);
    };
  }, [accessToken, dispatch]);
};

/**
 * Remaining milliseconds before the automatic logout, or null when not logged in.
 * Read-only — it does not start a watcher.
 */
export const useInactivityRemaining = () => {
  const accessToken = useSelector((state) => state.auth.accessToken);
  const lastActionAt = useSelector((state) => state.auth.lastActionAt);

  if (!accessToken || !lastActionAt) return null;
  return Math.max(0, INACTIVITY_TIMEOUT - (Date.now() - lastActionAt));
};
